import { useEffect, useId, useMemo, useState } from 'react';
import { Button, Header, Icon, Message, Segment, Table } from 'semantic-ui-react';
import { describe } from '../approvals.js';
import { MONTH_NAMES, monthOfAsAt } from '../periods.js';
import { buildSummary, filterRows, fmt, latestPerReport, totalsOf } from '../summary.js';

const NO_FILTER = { partner: '', reportId: '', month: 'all' };

/* Every report as one row per month, January up to the chosen month, with the line items (sites, sockets, financials) as columns.
 * Open to everyone, whoever the account manager is. */
export default function SummaryTab({ reports, approvals, loadFull, onExcel, busy }) {
  const years = useMemo(() => [...new Set(reports.map((r) => r.report_year))].sort((a, b) => b - a), [reports]);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [filters, setFilters] = useState(NO_FILTER);
  const [entries, setEntries] = useState(null);                    // [{ report, figures }] for the chosen year
  const [error, setError] = useState(null);
  const uid = useId();

  const y = year ?? years[0] ?? null;
  const picked = useMemo(() => (y === null ? [] : latestPerReport(reports, y)), [reports, y]);
  const m = month ?? (picked.length ? Math.max(...picked.map((r) => monthOfAsAt(r.as_at_date))) : 0);

  useEffect(() => {
    let live = true;
    setEntries(null); setError(null);
    Promise.all(picked.map((r) => loadFull(r))).then((all) => { if (live) setEntries(all); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [picked, loadFull]);

  const summary = useMemo(() => (entries && y !== null ? buildSummary(entries, y, m, approvals) : null), [entries, y, m, approvals]);
  const only = filters.month !== 'all' && Number(filters.month) <= m ? filters.month : 'all';   // a month filter past the chosen month no longer applies
  const shown = useMemo(() => (summary ? filterRows(summary.rows, { ...filters, month: only }) : []), [summary, filters, only]);
  const totals = useMemo(() => (summary ? totalsOf(summary.columns, shown) : {}), [summary, shown]);
  const partners = useMemo(() => [...new Set((summary?.rows || []).map((r) => r.partner).filter(Boolean))].sort(), [summary]);
  const ids = useMemo(() => [...new Set((summary?.rows || []).map((r) => r.id))].sort(), [summary]);
  const filtered = !!(filters.partner.trim() || filters.reportId.trim() || only !== 'all');
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <section aria-labelledby="sum-heading">
      <Header as="h2" id="sum-heading" className="titlebar">
        <Icon name="table" />
        <Header.Content>Summary<Header.Subheader>Every report, month by month from January up to the month chosen; by-site figures are summed over the sites</Header.Subheader></Header.Content>
      </Header>
      <div className="toolbar" role="group" aria-label="Choose the summary period">
        <label htmlFor="sum-year">Year</label>
        <select id="sum-year" className="ui dropdown" value={y ?? ''} onChange={(e) => { setYear(Number(e.target.value)); setMonth(null); }}>
          {years.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <label htmlFor="sum-month">Month</label>
        <select id="sum-month" className="ui dropdown" value={m} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTH_NAMES.map((n, i) => <option key={n} value={i}>{n}</option>)}
        </select>
        <Button type="button" icon="file excel outline" labelPosition="left" content="Download to Excel" loading={busy === 'summary'}
          disabled={!summary || !shown.length || !!busy} onClick={() => onExcel({ ...summary, rows: shown, totals, filters: { ...filters, month: only } })} />
      </div>
      <div className="toolbar filterbar" role="search" aria-label="Filter the summary">
        <span className="fgroup">
          <label htmlFor={`${uid}-partner`}>Partner</label>
          <input id={`${uid}-partner`} type="search" className="filterinput" list={`${uid}-partners`} placeholder="Any partner" autoComplete="off"
            value={filters.partner} onChange={(e) => set({ partner: e.target.value })} />
          <datalist id={`${uid}-partners`}>{partners.map((p) => <option key={p} value={p} />)}</datalist>
        </span>
        <span className="fgroup">
          <label htmlFor={`${uid}-report`}>Report ID</label>
          <input id={`${uid}-report`} type="search" className="filterinput short" list={`${uid}-ids`} placeholder="Any report" autoComplete="off"
            value={filters.reportId} onChange={(e) => set({ reportId: e.target.value })} />
          <datalist id={`${uid}-ids`}>{ids.map((p) => <option key={p} value={p} />)}</datalist>
        </span>
        <span className="fgroup">
          <label htmlFor={`${uid}-only`}>Only month</label>
          <select id={`${uid}-only`} className="ui dropdown" value={only} onChange={(e) => set({ month: e.target.value })}>
            <option value="all">All months</option>
            {MONTH_NAMES.slice(0, m + 1).map((n, i) => <option key={n} value={i}>{n}</option>)}
          </select>
        </span>
        {filtered && <Button type="button" basic compact size="small" icon="close" labelPosition="left" content="Clear filters" onClick={() => setFilters(NO_FILTER)} />}
      </div>
      {error && <Message negative role="alert">{error}</Message>}
      {!error && !summary && y !== null && <Message info role="status">Loading reports…</Message>}
      {y === null && <Segment placeholder><Header icon><Icon name="folder open outline" />There are no reports yet.</Header></Segment>}
      {summary && summary.rows.length === 0 && <Message info>No figures for {y} up to {MONTH_NAMES[m]}.</Message>}
      {summary && summary.rows.length > 0 && (
        <>
          <p className="hint rowcount" role="status">Showing {shown.length} of {summary.rows.length} rows. Months in which a report has no figures yet are left out.</p>
          {shown.length === 0 ? <Message info>No rows match the filters.</Message> : (
            <div className="gridwrap" tabIndex={0} role="region" aria-label={`${summary.title} (scrollable)`}>
              <Table celled compact unstackable className="datagrid sumtable">
                <caption className="visually-hidden">{summary.title}. One row per report and month; one column per line item. By-site figures are summed over the sites.</caption>
                <Table.Header>
                  <Table.Row className="grouprow">
                    <Table.HeaderCell scope="col" rowSpan={2} className="lab c1">Report</Table.HeaderCell>
                    <Table.HeaderCell scope="col" rowSpan={2} className="c2">Month</Table.HeaderCell>
                    {summary.groups.map((g) => <Table.HeaderCell scope="colgroup" key={g.section} colSpan={g.span} className={`grp ${g.section}`}>{g.title}</Table.HeaderCell>)}
                    <Table.HeaderCell scope="colgroup" colSpan={3} className="grp status">Owner &amp; approval</Table.HeaderCell>
                  </Table.Row>
                  <Table.Row>
                    {summary.columns.map((c) => <Table.HeaderCell scope="col" key={c.key} textAlign="center" className="metric">{c.label}</Table.HeaderCell>)}
                    <Table.HeaderCell scope="col" className="metric">Account manager</Table.HeaderCell>
                    <Table.HeaderCell scope="col" className="metric">Finance approval</Table.HeaderCell>
                    <Table.HeaderCell scope="col" className="metric">AM approval</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {shown.map((r, i) => (
                    <Table.Row key={r.key} className={i === 0 || shown[i - 1].reportKey !== r.reportKey ? 'newgroup' : undefined}>
                      <Table.HeaderCell scope="row" className="lab c1" title={`${r.id} – ${r.partner}`}>{r.id}<div className="sub">{r.partner}</div></Table.HeaderCell>
                      <Table.Cell className="c2">{r.month}</Table.Cell>
                      {summary.columns.map((c) => <Table.Cell key={c.key} textAlign="right">{fmt(c.kind, r.values[c.key]) || '–'}</Table.Cell>)}
                      <Table.Cell>{r.manager || '–'}</Table.Cell>
                      <Table.Cell className="approvalcell">{describe(r.finance) || <span className="hint">Pending</span>}</Table.Cell>
                      <Table.Cell className="approvalcell">{describe(r.account_manager) || <span className="hint">Pending</span>}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Footer>
                  <Table.Row className="totalrow">
                    <Table.HeaderCell scope="row" className="lab c1">Total ({shown.length} row{shown.length === 1 ? '' : 's'})</Table.HeaderCell>
                    <Table.HeaderCell className="c2" />
                    {summary.columns.map((c) => <Table.HeaderCell key={c.key} textAlign="right">{fmt(c.kind, totals[c.key]) || '–'}</Table.HeaderCell>)}
                    <Table.HeaderCell colSpan={3} />
                  </Table.Row>
                </Table.Footer>
              </Table>
            </div>
          )}
          <p className="hint">Totals add up the rows shown. Percentages are never added, and site / chargepoint / socket counts are added only when the rows are all one month.</p>
        </>
      )}
    </section>
  );
}
